import * as THREE from 'three';
import { bus } from '../core/bus.js';
import { bakeToSingleMesh } from '../core/meshUtils.js';
import { FBXLoader } from 'three/addons/loaders/FBXLoader.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { OBJLoader } from 'three/addons/loaders/OBJLoader.js';
import { MTLLoader } from 'three/addons/loaders/MTLLoader.js';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import * as BufferGeometryUtils from 'three/addons/utils/BufferGeometryUtils.js';
import { FBX_DIR, FBX_MODELS, GLB_MODELS, GLB_TINT, OBJ_MODELS, SPECIES } from './animalCatalog.js';

// meshoptimizer (WASM) simplifier — high-quality LOD, generated ONCE per species at load.
let _meshopt = null;
import('meshoptimizer/meshopt_simplifier.module.js')
  .then((m) => m.MeshoptSimplifier.ready.then(() => { _meshopt = m.MeshoptSimplifier; }))
  .catch((e) => console.warn('[lod] meshopt unavailable, using procedural mid', e));

function simplifyGeometry(geo, ratio) {
  if (!_meshopt) return null;
  let g = geo.index ? geo.clone() : BufferGeometryUtils.mergeVertices(geo.clone());
  const pos = g.attributes.position;
  const idx = g.index.array;
  const srcIdx = idx instanceof Uint32Array ? idx : new Uint32Array(idx);
  const target = Math.max(12, Math.floor((idx.length * ratio) / 3) * 3);
  const [dst] = _meshopt.simplify(srcIdx, pos.array, pos.count, 3, target, 0.05, ['LockBorder']);
  if (!dst || dst.length >= idx.length) return null;
  g.setIndex(new THREE.BufferAttribute(dst, 1));
  return g;
}

function simplifyModel(src, ratio) {
  const clone = SkeletonUtils.clone(src);
  clone.traverse((o) => {
    if (o.isMesh && o.geometry) { const s = simplifyGeometry(o.geometry, ratio); if (s) o.geometry = s; }
  });
  return clone;
}

// Build real meshopt LODs for every loaded GLB/FBX species (mid ~50%), cached for buildLowModel.
const LOD_MID = new Map();
async function prepareAnimalLODs() {
  for (let i = 0; i < 60 && !_meshopt; i++) await new Promise((r) => setTimeout(r, 100));
  if (!_meshopt) { window.__lodStatus = { meshopt: false, mid: 0 }; return; }
  for (const [key, model] of FBX_CACHE) {
    try { LOD_MID.set(key, simplifyModel(model, 0.5)); } catch (e) { /* fallback to procedural */ }
  }
  window.__lodStatus = { meshopt: true, mid: LOD_MID.size, keys: [...LOD_MID.keys()] };
  // Force animals already showing the mid LOD to rebuild with the new meshopt mesh.
  const w = window.__world;
  if (w && w.animals) for (const a of w.animals.animals) { if ((a.lod || 'hi') === 'lo') a.lod = '_stale'; }
}

const FBX_CACHE = new Map(); // species -> normalized master group

export function hasRealAnimalModel(speciesKey) {
  return FBX_CACHE.has(speciesKey);
}

async function preloadAnimalModels() {
  const loader = new FBXLoader();  await Promise.all(Object.entries(FBX_MODELS).map(async ([key, file]) => {
    try {
      const fbx = await loader.loadAsync(FBX_DIR + file);
      fbx.updateMatrixWorld(true);
      fbx.traverse((o) => {
        if (!o.isMesh) return;
        o.castShadow = true;
        const fix = (m) => {
          const nm = new THREE.MeshStandardMaterial({ color: m.color ? m.color.clone() : new THREE.Color(0xbbaa99), map: m.map || null, roughness: 0.85 });
          if (nm.map) nm.map.colorSpace = THREE.SRGBColorSpace;
          return nm;
        };
        o.material = Array.isArray(o.material) ? o.material.map(fix) : fix(o.material);
      });
      // normalize: height = species size, feet on y=0
      const sp = SPECIES[key];
      const bb = new THREE.Box3().setFromObject(fbx);
      const h = Math.max(bb.max.y - bb.min.y, 0.001);
      const scale = (sp ? sp.size * 1.15 : 1.2) / h;
      const container = new THREE.Group();
      fbx.scale.setScalar(scale);
      fbx.position.y = -bb.min.y * scale;
      container.add(fbx);
      FBX_CACHE.set(key, container);
      bus.emit('animals:modelReady', key);
    } catch (e) { console.warn('[animals] FBX load failed for', key, e); }
  }));
  await Promise.all(Object.entries(GLB_MODELS).map(async ([key, url]) => {
    try {
      const gltf = await new GLTFLoader().loadAsync(url);
      gltf.scene.updateMatrixWorld(true);
      gltf.scene.traverse((o) => {
        if (!o.isMesh) return;
        o.castShadow = true;
        if (GLB_TINT[key]) o.material = new THREE.MeshStandardMaterial({ color: GLB_TINT[key], roughness: 0.8 });
        else if (o.material && o.material.map) o.material.map.colorSpace = THREE.SRGBColorSpace;
      });
      const sp = SPECIES[key];
      const bb = new THREE.Box3().setFromObject(gltf.scene);
      const h = Math.max(bb.max.y - bb.min.y, 0.001);
      const scale = (sp ? sp.size * 1.15 : 1.3) / h;
      const container = new THREE.Group();
      gltf.scene.scale.setScalar(scale);
      gltf.scene.position.y = -bb.min.y * scale;
      container.add(gltf.scene);
      FBX_CACHE.set(key, container);
      bus.emit('animals:modelReady', key);
    } catch (e) { console.warn('[animals] GLB load failed for', key, e); }
  }));
  await Promise.all(Object.entries(OBJ_MODELS).map(async ([key, dir]) => {
    try {
      const ml = new MTLLoader(); ml.setPath(dir); ml.setResourcePath(dir);
      const mtl = await ml.loadAsync('Mesh_Koala.mtl'); mtl.preload();
      const ol = new OBJLoader(); ol.setMaterials(mtl); ol.setPath(dir);
      const obj = await ol.loadAsync('Mesh_Koala.obj');
      obj.updateMatrixWorld(true);
      obj.traverse((o) => { if (o.isMesh) { o.castShadow = true; const mm = Array.isArray(o.material) ? o.material : [o.material]; mm.forEach((m) => { if (m && m.map) m.map.colorSpace = THREE.SRGBColorSpace; }); } });
      const sp = SPECIES[key];
      const bb = new THREE.Box3().setFromObject(obj);
      const h = Math.max(bb.max.y - bb.min.y, 0.001);
      const scale = (sp ? sp.size * 1.15 : 1.0) / h;
      const container = new THREE.Group();
      obj.scale.setScalar(scale);
      obj.position.set(-((bb.min.x + bb.max.x) / 2) * scale, -bb.min.y * scale, -((bb.min.z + bb.max.z) / 2) * scale);
      container.add(obj);
      FBX_CACHE.set(key, container);
      bus.emit('animals:modelReady', key);
    } catch (e) { console.warn('[animals] OBJ load failed for', key, e); }
  }));
}

// Small buy-menu thumbnails: render each species' model once to a data URL.
export const ANIMAL_THUMBS = {};
function generateAnimalThumbnails() {
  const S = 128;
  const r = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  r.setSize(S, S); r.setPixelRatio(1);
  const scene = new THREE.Scene();
  scene.add(new THREE.HemisphereLight(0xffffff, 0x8899aa, 1.25));
  const dl = new THREE.DirectionalLight(0xfff0d8, 2.4); dl.position.set(3, 6, 4); scene.add(dl);
  const cam = new THREE.PerspectiveCamera(38, 1, 0.1, 100);
  for (const key of Object.keys(SPECIES)) {
    let model; try { model = buildModel(key); } catch (e) { continue; }
    const bb = new THREE.Box3().setFromObject(model);
    const size = bb.getSize(new THREE.Vector3());
    const ctr = bb.getCenter(new THREE.Vector3());
    const maxd = Math.max(size.x, size.y, size.z) || 1;
    model.position.sub(ctr);
    scene.add(model);
    const rad = maxd * 1.45;
    cam.position.set(rad * 0.75, rad * 0.5, rad); cam.lookAt(0, 0, 0);
    r.render(scene, cam);
    ANIMAL_THUMBS[key] = r.domElement.toDataURL('image/png');
    scene.remove(model);
  }
  r.dispose();
  bus.emit('animals:thumbs');
}

preloadAnimalModels().then(generateAnimalThumbnails).then(prepareAnimalLODs);

const MODEL_CACHE = new Map(); // species -> baked single-mesh master (cloned per instance)

export function buildModel(speciesKey) {
  if (FBX_CACHE.has(speciesKey)) return SkeletonUtils.clone(FBX_CACHE.get(speciesKey));
  if (!MODEL_CACHE.has(speciesKey)) MODEL_CACHE.set(speciesKey, bakeToSingleMesh(rawBuildModel(speciesKey)));
  return MODEL_CACHE.get(speciesKey).clone();
}

const ANIMAL_IMP = new Map();
// Camera-facing billboard impostor for far animals: render the model once (3/4 side, lit like the trees).
let _bakeR = null;
// One SHARED offscreen renderer for baking impostors (reused across all species). Creating a new
// WebGLRenderer per species blows past the browser's ~16-context limit -> the main context is lost.
function _impostorBaker(S) {
  if (!_bakeR) {
    _bakeR = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    _bakeR.setPixelRatio(1); _bakeR.outputColorSpace = THREE.SRGBColorSpace;
    _bakeR.toneMapping = THREE.ACESFilmicToneMapping; _bakeR.toneMappingExposure = 1.4;
  }
  _bakeR.setSize(S, S);
  return _bakeR;
}

export function animalImpostor(speciesKey) {
  if (ANIMAL_IMP.has(speciesKey)) return ANIMAL_IMP.get(speciesKey);
  const S = 128;
  const r = _impostorBaker(S);
  const scene = new THREE.Scene();
  scene.add(new THREE.HemisphereLight(0xeaf6f0, 0xcfe6c0, 0.8));
  scene.add(new THREE.AmbientLight(0xffffff, 1.2));
  const sun = new THREE.DirectionalLight(0xfff3e0, 1.6); sun.position.set(2, 3, 4); scene.add(sun);
  const model = buildModel(speciesKey);
  const box = new THREE.Box3().setFromObject(model), ctr = box.getCenter(new THREE.Vector3()), size = box.getSize(new THREE.Vector3());
  model.position.sub(ctr); scene.add(model);
  const md = Math.max(size.x, size.y, size.z) || 1;
  const cam = new THREE.OrthographicCamera(-md * 0.62, md * 0.62, md * 0.62, -md * 0.62, 0.1, 100);
  cam.position.set(md * 1.4, md * 0.5, md * 2); cam.lookAt(0, 0, 0);
  r.render(scene, cam);
  const cv = document.createElement('canvas'); cv.width = S; cv.height = S; cv.getContext('2d').drawImage(r.domElement, 0, 0);
  const tex = new THREE.CanvasTexture(cv); tex.colorSpace = THREE.SRGBColorSpace;
  const rec = { tex, w: Math.max(size.x, size.z) || 1, h: size.y || 1 };
  ANIMAL_IMP.set(speciesKey, rec); return rec;
}

// Low-poly LOD proxy: real meshopt-simplified model (~50%) when available, else the procedural bake.
function buildLowModel(speciesKey) {
  if (LOD_MID.has(speciesKey)) return SkeletonUtils.clone(LOD_MID.get(speciesKey));
  const key = 'lo:' + speciesKey;
  if (!MODEL_CACHE.has(key)) MODEL_CACHE.set(key, bakeToSingleMesh(rawBuildModel(speciesKey)));
  return MODEL_CACHE.get(key).clone();
}

// Cheapest LOD: one coloured block for very distant animals (~12 tris).
function buildBlockModel(speciesKey) {
  const key = 'blk:' + speciesKey;
  if (!MODEL_CACHE.has(key)) {
    const sp = SPECIES[speciesKey];
    const g = new THREE.BoxGeometry(0.9, 0.9, 1.5); g.translate(0, 0.55, 0);
    const m = new THREE.Mesh(g, new THREE.MeshStandardMaterial({ color: (sp && sp.color) || 0x888888, roughness: 0.9 }));
    m.castShadow = false;
    MODEL_CACHE.set(key, m);
  }
  return MODEL_CACHE.get(key).clone();
}

const _billTex = {};
function billboardTexture(key) {
  if (_billTex[key] === undefined) {
    const url = ANIMAL_THUMBS[key];
    if (!url) return null;
    const t = new THREE.TextureLoader().load(url);
    t.colorSpace = THREE.SRGBColorSpace;
    _billTex[key] = t;
  }
  return _billTex[key];
}

// Far LOD: a billboard impostor — the species rendered to a texture on a camera-facing sprite
// (2 tris, keeps the real look). Falls back to the block if the thumbnail isn't ready yet.
function buildBillboardModel(speciesKey) {
  const tex = billboardTexture(speciesKey);
  if (!tex) return buildBlockModel(speciesKey);
  const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, transparent: true, depthWrite: false }));
  const sp = SPECIES[speciesKey];
  const sz = 3.2 * (sp ? sp.size : 1);
  s.scale.set(sz, sz, 1);
  s.position.y = sz * 0.5;
  const g = new THREE.Group();
  g.add(s);
  return g;
}

export function buildLOD(species, lod) {
  return lod === 'bill' ? buildBillboardModel(species) : lod === 'lo' ? buildLowModel(species) : buildModel(species);
}

// Merge up to 5 same-species figures into ONE mesh (one draw call per herd group).
const ANIMAL_OFFS = [[[0, 0]], [[-0.6, 0], [0.6, 0]], [[-0.7, -0.4], [0.7, -0.4], [0, 0.6]], [[-0.7, -0.5], [0.7, -0.5], [-0.7, 0.5], [0.7, 0.5]], [[-0.8, -0.5], [0.8, -0.5], [-0.8, 0.5], [0.8, 0.5], [0, 0]]];
export function buildAnimalGroup(species, n) {
  n = Math.max(1, Math.min(5, n));
  if (n === 1) return buildModel(species);
  const spread = 0.7 + (SPECIES[species] ? SPECIES[species].size : 1) * 0.7;
  const g = new THREE.Group();
  ANIMAL_OFFS[n - 1].forEach(([ox, oz]) => {
    const fig = buildModel(species);
    fig.position.set(ox * spread, 0, oz * spread);
    fig.rotation.y = Math.random() * Math.PI * 2;
    g.add(fig);
  });
  return bakeToSingleMesh(g);
}

function rawBuildModel(speciesKey) {
  const sp = SPECIES[speciesKey];
  const g = new THREE.Group();
  const bodyMat = new THREE.MeshStandardMaterial({ color: sp.color, roughness: 0.85 });
  const accentMat = new THREE.MeshStandardMaterial({ color: sp.accent, roughness: 0.85 });
  const s = sp.size;

  const add = (geo, mat, x, y, z, rx = 0, rz = 0) => {
    const m = new THREE.Mesh(geo, mat);
    m.position.set(x, y, z);
    m.rotation.x = rx; m.rotation.z = rz;
    m.castShadow = true;
    g.add(m);
    return m;
  };

  const BIRDS = ['flamingo', 'ostrich', 'penguin', 'emu', 'goose'];
  if (BIRDS.includes(speciesKey)) {
    const squat = speciesKey === 'penguin' ? 0.55 : 1;
    add(new THREE.SphereGeometry(0.32 * s * 2, 8, 6), bodyMat, 0, 1.0 * s * squat, 0);
    add(new THREE.CylinderGeometry(0.03, 0.03, 1.0 * s * squat, 5), accentMat, 0.1 * s, 0.5 * s * squat, 0);
    add(new THREE.CylinderGeometry(0.03, 0.03, 1.0 * s * squat, 5), accentMat, -0.1 * s, 0.5 * s * squat, 0);
    add(new THREE.CylinderGeometry(0.05, 0.07, 0.9 * s * squat, 5), speciesKey === 'penguin' ? accentMat : bodyMat, 0, 1.5 * s * squat, 0.15 * s, 0.5);
    add(new THREE.SphereGeometry(0.12 * s * 2, 6, 5), bodyMat, 0, 1.95 * s * squat, 0.42 * s);
    add(new THREE.ConeGeometry(0.05, 0.3 * s, 5), accentMat, 0, 1.9 * s * squat, 0.6 * s, Math.PI / 2);
  } else {
    // quadruped base
    const bodyLen = 1.6 * s, bodyH = 0.62 * s, legH = 0.7 * s;
    add(new THREE.BoxGeometry(bodyH, bodyH, bodyLen), bodyMat, 0, legH + bodyH / 2, 0);
    for (const dx of [-1, 1]) for (const dz of [-1, 1]) {
      add(new THREE.CylinderGeometry(0.09 * s, 0.11 * s, legH, 6), bodyMat, dx * bodyH * 0.32, legH / 2, dz * bodyLen * 0.36);
    }
    if (speciesKey === 'giraffe') {
      add(new THREE.CylinderGeometry(0.12 * s, 0.18 * s, 1.4 * s, 6), bodyMat, 0, legH + bodyH + 0.6 * s, bodyLen * 0.42, 0.35);
      add(new THREE.BoxGeometry(0.24 * s, 0.24 * s, 0.5 * s), accentMat, 0, legH + bodyH + 1.35 * s, bodyLen * 0.62);
    } else if (speciesKey === 'elephant') {
      add(new THREE.SphereGeometry(0.42 * s, 8, 6), bodyMat, 0, legH + bodyH * 0.9, bodyLen * 0.55);
      add(new THREE.CylinderGeometry(0.07 * s, 0.1 * s, 0.9 * s, 6), bodyMat, 0, legH + bodyH * 0.45, bodyLen * 0.72, 0.5);
      add(new THREE.SphereGeometry(0.26 * s, 6, 5), accentMat, 0.4 * s, legH + bodyH, bodyLen * 0.5);
      add(new THREE.SphereGeometry(0.26 * s, 6, 5), accentMat, -0.4 * s, legH + bodyH, bodyLen * 0.5);
    } else {
      // generic head (lion, zebra, hippo, gorilla, panda)
      add(new THREE.BoxGeometry(0.34 * s, 0.36 * s, 0.5 * s), (speciesKey === 'lion' || speciesKey === 'panda') ? accentMat : bodyMat, 0, legH + bodyH + 0.16 * s, bodyLen * 0.55);
      if (speciesKey === 'zebra') {
        for (let i = 0; i < 5; i++) add(new THREE.BoxGeometry(bodyH * 1.02, bodyH * 1.02, 0.06 * s), accentMat, 0, legH + bodyH / 2, -bodyLen * 0.35 + i * bodyLen * 0.17);
      }
      if (speciesKey === 'panda') {
        add(new THREE.SphereGeometry(0.1 * s, 5, 4), accentMat, 0.15 * s, legH + bodyH + 0.42 * s, bodyLen * 0.55);
        add(new THREE.SphereGeometry(0.1 * s, 5, 4), accentMat, -0.15 * s, legH + bodyH + 0.42 * s, bodyLen * 0.55);
      }
      if (speciesKey === 'gorilla') {
        add(new THREE.BoxGeometry(0.5 * s, 0.5 * s, 0.4 * s), bodyMat, 0, legH + bodyH + 0.3 * s, -bodyLen * 0.1); // broad shoulders
      }
    }
    // tail
    add(new THREE.CylinderGeometry(0.03 * s, 0.02 * s, 0.5 * s, 4), accentMat, 0, legH + bodyH * 0.8, -bodyLen * 0.55, 0.9);
  }
  return g;
}
